/**
 * KAN-752: "Новая кампания" - оператор-кампания без входа в систему.
 *
 * Название кампании становится именем оператора; после создания открывается
 * страница оператора на вкладке инструкций.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateCampaignOperator } from '@/hooks/useUsers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';

const NAME_MAX_LENGTH = 100;

interface CreateCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateCampaignDialog({ open, onOpenChange }: CreateCampaignDialogProps): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateCampaignOperator();
  const [name, setName] = useState('');
  const resetCreate = create.reset;

  // Каждое открытие начинается с пустой формы без прошлой ошибки.
  useEffect(() => {
    if (open) {
      setName('');
      resetCreate();
    }
  }, [open, resetCreate]);

  const trimmed = name.trim();
  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!trimmed) return;
    create.mutate(trimmed, {
      onSuccess: (user) => {
        onOpenChange(false);
        navigate(`/users/${user.id}?tab=instructions`);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-campaign-dialog">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Новая кампания</DialogTitle>
            <DialogDescription>
              Кампания - отдельный оператор рассылки со своими инструкциями и приветствиями. Вход в
              систему ему не нужен.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 space-y-2">
            <Label htmlFor="campaign-name">Название кампании</Label>
            <Input
              id="campaign-name"
              data-testid="campaign-name-input"
              value={name}
              maxLength={NAME_MAX_LENGTH}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например, Артикаин осень"
              autoFocus
            />
            {create.isError && (
              <p data-testid="campaign-create-error" className="text-xs text-destructive">
                {extractErrorMessage(create.error, 'Не удалось создать кампанию')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" data-testid="campaign-create-submit" disabled={!trimmed || create.isPending}>
              {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Создать
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateCampaignDialog;
